import React, { useState, useEffect } from 'react';
import { Download, Loader2, Menu } from 'lucide-react';
import { GuideSidebar } from '../components/guide/GuideSidebar';
import { GuideContent } from '../components/guide/GuideContent';
import { userGuideAPI } from '../api/endpoints';
import { UserGuideChapter } from '../types/userGuide';
import Layout from '../components/layout/Layout';

export const HelpGuide: React.FC = () => {
  const [chapters, setChapters] = useState<UserGuideChapter[]>([]);
  const [activeChapterId, setActiveChapterId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchGuide = async () => {
      try {
        const response = await userGuideAPI.getGuide();
        const data = response.data.data;
        setChapters(data);
        if (data.length > 0) {
          setActiveChapterId(data[0].id);
        }
      } catch (err: any) {
        setError('Failed to load the user guide. Please try again later.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGuide();
  }, []);

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      const response = await userGuideAPI.downloadGuidePdf();
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'EventFlow_User_Guide.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const activeChapter = chapters.find(c => c.id === activeChapterId);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex min-h-[500px] items-center justify-center">
          <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="p-8 text-center text-red-600 bg-white rounded-lg shadow-sm border border-gray-200 mt-4">
          {error}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col h-[calc(100vh-8rem)] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Header toolbar */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white z-10">
          <div className="flex items-center space-x-4">
            <button 
              className="md:hidden p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-semibold text-gray-800">User Guide</h1>
          </div>
          <button
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            <span className="hidden sm:inline">{isDownloading ? 'Generating PDF...' : 'Download PDF'}</span>
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden relative">
          {/* Mobile Sidebar Overlay */}
          {mobileMenuOpen && (
            <div className="absolute inset-0 bg-black bg-opacity-50 z-20 md:hidden" onClick={() => setMobileMenuOpen(false)} />
          )}
          
          {/* Mobile Sidebar */}
          <div className={`absolute inset-y-0 left-0 transform ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition duration-200 ease-in-out z-30 bg-white md:block md:w-64 border-r border-gray-200`}>
            <GuideSidebar 
              chapters={chapters} 
              activeChapterId={activeChapterId}
              onSelectChapter={(id) => {
                setActiveChapterId(id);
                setMobileMenuOpen(false);
              }} 
            />
          </div>

          <GuideContent chapter={activeChapter} />
        </div>
      </div>
    </Layout>
  );
};
